import { Tooltip } from 'antd'
import type { MouseEvent } from 'react'
import { toast } from '@crosscode/gravity-components'
import { NODE_META } from './constants'
import { DeviceActions, NODE_ICONS } from './DeviceActions'
import { formatPing, labelToType, type InfraNode } from './utils'
import styles from '@/pages/HomePage.module.css'

function copyIp(ip?: string) {
  if (!ip) return
  void navigator.clipboard?.writeText(ip)
  toast.success('IP скопирован')
}

interface Props {
  node: InfraNode
  id: string
  storeId: string
  utm?: Record<string, unknown>
  chz?: Record<string, unknown>
  onReload: () => void
  onShowDetails?: () => void
}

export function DeviceGridCard({ node, id, storeId, utm, chz, onReload, onShowDetails }: Props) {
  const nodeType = labelToType(node.label || node.name, node.type)
  const meta = NODE_META[nodeType]
  const title = node.name || node.label || meta.titleHint
  const online = node.status === 'online'
  const offline = node.status === 'offline'
  const ping = formatPing(node.ms)
  const hasMetaError = Boolean(utm?.error || chz?.error)

  const summaryMeta = (() => {
    if (nodeType === 'chz' && chz && !chz.error) return `v${String(chz.version ?? '—')}`
    if (nodeType === 'egais_utm' && utm && !utm.error) {
      return `v${String(utm.version ?? '—')}${utm.contour != null ? ` · ${String(utm.contour)}` : ''}`
    }
    if (hasMetaError && (nodeType === 'egais_utm' || nodeType === 'chz')) {
      return String(utm?.error || chz?.error)
    }
    return null
  })()

  const statusText = offline ? 'Оффлайн' : online ? 'Онлайн' : 'Неизвестно'
  const stop = (e: MouseEvent) => e.stopPropagation()

  return (
    <article
      className={`${styles.deviceCard} ${offline ? styles.deviceCardOffline : online ? styles.deviceCardOnline : ''}`}
    >
      <div className={styles.deviceCardHead}>
        <span className={styles.deviceCardIcon}>{NODE_ICONS[nodeType]}</span>
        <div className={styles.deviceCardTitle}>
          <strong>{title}</strong>
          <span className={styles.typeChip}>{meta.titleHint}</span>
        </div>
        <span
          className={[
            styles.statusCell,
            online ? styles.statusOn : '',
            offline ? styles.statusOff : '',
            !online && !offline ? styles.statusMuted : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <i className={styles.statusDot} aria-hidden />
          {statusText}
        </span>
      </div>
      <div className={styles.deviceCardMeta}>
        <Tooltip title="Скопировать IP">
          <button
            type="button"
            className={styles.ipBtn}
            onClick={(e) => {
              stop(e)
              copyIp(node.ip)
            }}
          >
            {node.ip || '—'}
          </button>
        </Tooltip>
        {ping ? <span className={styles.statusPing}>{ping}</span> : null}
        {summaryMeta ? (
          <span className={`${styles.metaChip} ${hasMetaError ? styles.metaErr : ''}`} title={summaryMeta}>
            {summaryMeta}
          </span>
        ) : null}
      </div>
      <div className={styles.deviceCardActions} onClick={stop}>
        <DeviceActions
          node={node}
          id={id}
          storeId={storeId}
          chz={chz}
          onReload={onReload}
          onShowDetails={onShowDetails}
        />
      </div>
    </article>
  )
}
